import "./style.css";
import { type MrbdSseEvent, type RecordingState, type SessionResponse } from "@mrbd/contracts";
import { ActivationGate } from "./activation-gate.js";
import { AnswerScroller, type ScrollSnapshot } from "./answer-scroll.js";
import {
  ApiError,
  getModels,
  getSession,
  logout,
  redeemPairing,
  sendDiagnostics,
  streamTurn,
  type ContextTurn,
  type DiagnosticEvent,
} from "./api.js";
import { focusables, installDpad } from "./dpad.js";
import { installLifecycleSafety } from "./lifecycle.js";
import { RecorderController, type RecordedAudio, type RecorderDiagnostics } from "./recorder.js";
import { renderStructuredText } from "./safe-render.js";

const $ = <T extends HTMLElement>(id: string): T => {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing element #${id}`);
  return element as T;
};

const screens = {
  boot: $("boot-screen"),
  pairing: $("pairing-screen"),
  home: $("home-screen"),
  settings: $("settings-screen"),
};
const recordButton = $<HTMLButtonElement>("record-button");
const recordLabel = $("record-label");
const recordTimer = $("record-timer");
const statusText = $("status-text");
const transcription = $("transcription");
const answerPanel = $("answer-panel");
const answerContent = $("answer-content");
const streamStatus = $("stream-status");
const scrollProgress = $("scroll-progress");
const newTextButton = $<HTMLButtonElement>("new-text-button");
const retryButton = $<HTMLButtonElement>("retry-button");
const cancelButton = $<HTMLButtonElement>("cancel-button");
const pairingError = $("pairing-error");
const diagnosticView = $("diagnostics");
const answerScroller = new AnswerScroller(answerPanel);
const activationGate = new ActivationGate(750);
const pageInstanceId = crypto.randomUUID();

let currentScreen: keyof typeof screens = "boot";
let session: SessionResponse | null = null;
let csrfToken = "";
let pairingDigits = [0, 0, 0, 0, 0, 0];
let abortController: AbortController | null = null;
let lastTranscript = "";
let lastRecordedAudio: RecordedAudio | null = null;
let answerText = "";
let history: ContextTurn[] = [];
let completedReceived = false;
let deltaBuffer = "";
let deltaTimer: number | undefined;
let diagnosticQueue: DiagnosticEvent[] = [];
let diagnosticTimer: number | undefined;
let recorderDiagnostics: RecorderDiagnostics = {
  apiPresent: typeof navigator.mediaDevices?.getUserMedia === "function" && typeof globalThis.MediaRecorder === "function",
  mimeType: "not selected",
  dataAvailable: false,
  audioBytes: 0,
  stopEvent: false,
};

const recorder = new RecorderController({
  onState: updateRecordingState,
  onDiagnostics: (diagnostics) => {
    recorderDiagnostics = diagnostics;
    if (diagnostics.dataAvailable || diagnostics.errorName) {
      queueDiagnostic({
        event: "recorder-data",
        pageInstanceId,
        mimeType: diagnostics.mimeType,
        audioBytes: diagnostics.audioBytes,
        dataAvailable: diagnostics.dataAvailable,
        stopEvent: diagnostics.stopEvent,
        ...(diagnostics.errorName ? { errorName: diagnostics.errorName } : {}),
      });
    }
  },
  onTick: (elapsedMs) => { recordTimer.textContent = formatDuration(elapsedMs); },
  onAudio: uploadRecordedAudio,
  onError: (error) => showTurnError(microphoneErrorMessage(error), true),
});

const STATE_LABELS: Readonly<Record<RecordingState, string>> = {
  idle: "Start Recording",
  "requesting-permission": "Connecting…",
  recording: "Stop Recording",
  stopping: "Stopping…",
  uploading: "Uploading…",
  transcribing: "Transcribing…",
  "waiting-for-model": "Thinking…",
  streaming: "Streaming…",
  completed: "Start Recording",
  error: "Try Again",
  cancelled: "Start Recording",
};

function moveAppState(next: RecordingState): void {
  if (recorder.state === next) {
    updateRecordingState(next);
    return;
  }
  if (!recorder.machine.canTransition(next)) return;
  recorder.transition(next);
}

function showScreen(name: keyof typeof screens, focusSelector?: string): void {
  Object.values(screens).forEach((screen) => screen.classList.add("hidden"));
  screens[name].classList.remove("hidden");
  currentScreen = name;
  requestAnimationFrame(() => {
    const focus = focusSelector ? screens[name].querySelector<HTMLElement>(focusSelector) : focusables(screens[name])[0];
    focus?.focus();
  });
}

async function initialize(): Promise<void> {
  installActions();
  installDpad({
    getRoot: () => screens[currentScreen],
    onEscape: () => {
      if (currentScreen === "settings") showScreen("home", "#record-button");
      else if (isTurnActive()) cancelActive("escape");
    },
    onAnswerScroll: (direction) => renderScroll(answerScroller.scroll(direction)),
    onDigitChange: changeDigit,
  });
  installLifecycleSafety({
    windowTarget: window,
    documentTarget: document,
    stopRecording: (reason) => recorder.cancel(reason),
    cancelTurn: (reason) => cancelActive(reason),
    isRecordingActive: () => ["recording", "requesting-permission"].includes(recorder.state),
  });
  window.addEventListener("blur", () => queueDiagnostic({ event: "lifecycle", pageInstanceId, lifecycle: "blur" }));
  window.addEventListener("focus", () => queueDiagnostic({ event: "lifecycle", pageInstanceId, lifecycle: "focus" }));
  window.addEventListener("pagehide", () => queueDiagnostic({ event: "lifecycle", pageInstanceId, lifecycle: "pagehide" }));
  document.addEventListener("visibilitychange", () => queueDiagnostic({ event: "lifecycle", pageInstanceId, lifecycle: document.visibilityState === "hidden" ? "hidden" : "visible" }));
  answerPanel.addEventListener("scroll", () => renderScroll(answerScroller.snapshot()), { passive: true });
  window.addEventListener("online", () => updateNetworkState(true));
  window.addEventListener("offline", () => updateNetworkState(false));
  showScreen("home", "#record-button");
  statusText.textContent = "Restoring private session";
  $("current-user").textContent = "Restoring session…";
  await refreshSession();
}

async function refreshSession(): Promise<void> {
  try {
    const [restoredSession, models] = await Promise.all([getSession(), getModels().catch(() => [])]);
    session = restoredSession;
    if (!session.authenticated) {
      showScreen("pairing", "[data-digit-index='0']");
      return;
    }
    csrfToken = session.csrfToken ?? "";
    flushDiagnostics();
    renderSession(models);
    showScreen("home", "#record-button");
  } catch (error) {
    pairingError.textContent = safeMessage(error, "Could not reach the private server.");
    pairingError.classList.remove("hidden");
    showScreen("pairing", "[data-digit-index='0']");
  }
}

function renderSession(models: Awaited<ReturnType<typeof getModels>> = []): void {
  if (!session?.authenticated || !session.user || !session.settings) return;
  $("current-user").textContent = session.user.displayName;
  const catalogName = models.find((item) => item.provider === session!.settings!.provider && item.model === session!.settings!.model)?.displayName;
  $("current-model").textContent = `${session.settings.provider} · ${catalogName ?? session.settings.model}`;
  $("answer-model").textContent = session.settings.model;
  $("settings-user").textContent = session.user.displayName;
  $("settings-model").textContent = `${session.settings.provider} / ${session.settings.model}`;
  $("settings-language").textContent = session.settings.language;
  $("settings-version").textContent = `${session.appVersion} · ${session.buildCommit}`;
  $("settings-provider-calls").textContent = session.providerCallsEnabled ? "Enabled" : "Disabled on server";
}

function installActions(): void {
  document.querySelectorAll<HTMLElement>("[data-action]").forEach((element) => {
    element.addEventListener("keydown", (event) => activationGate.runKeyboard(event, () => void runAction(element.dataset.action ?? "", element)));
    element.addEventListener("click", (event) => activationGate.runClick(event, () => void runAction(element.dataset.action ?? "", element)));
  });
}

async function runAction(action: string, element: HTMLElement): Promise<void> {
  switch (action) {
    case "increment-digit": changeDigit(Number(element.dataset.digitIndex), 1); break;
    case "redeem-pairing": await pairDevice(); break;
    case "toggle-recording": await toggleRecording(); break;
    case "cancel": cancelActive("user"); break;
    case "retry": await retryTurn(); break;
    case "new-conversation": newConversation(); break;
    case "jump-latest": renderScroll(answerScroller.jumpLatest()); break;
    case "open-settings": void renderDiagnostics(); showScreen("settings"); break;
    case "back-home": showScreen("home", "#record-button"); break;
    case "logout": await logoutDevice(); break;
  }
}

function changeDigit(index: number, delta: number): void {
  pairingDigits[index] = ((pairingDigits[index] ?? 0) + delta + 10) % 10;
  const element = screens.pairing.querySelector<HTMLElement>(`[data-digit-index="${index}"]`);
  if (element) element.textContent = String(pairingDigits[index]);
}

async function pairDevice(): Promise<void> {
  pairingError.classList.add("hidden");
  const pairButton = screens.pairing.querySelector<HTMLButtonElement>("[data-action='redeem-pairing']");
  if (pairButton) pairButton.disabled = true;
  try {
    await redeemPairing(pairingDigits.join(""));
    await refreshSession();
  } catch (error) {
    pairingError.textContent = safeMessage(error, "Pairing failed. Check the code and try again.");
    pairingError.classList.remove("hidden");
  } finally {
    if (pairButton) pairButton.disabled = false;
  }
}

async function toggleRecording(): Promise<void> {
  if (recorder.state === "recording") {
    recorder.stop(false, "user");
    return;
  }
  if (["requesting-permission", "stopping", "uploading", "transcribing", "waiting-for-model", "streaming"].includes(recorder.state)) return;
  await startBrowserRecording();
}

async function startBrowserRecording(): Promise<void> {
  if (!csrfToken) return showTurnError("Private device session is unavailable. Pair again.", false);
  if (!session?.providerCallsEnabled) return showTurnError("Alibaba ASR and Qwen are not enabled on the server yet.", false);
  clearTurnForRecording();
  abortController?.abort("new-turn");
  await recorder.start();
}

async function uploadRecordedAudio(audio: RecordedAudio): Promise<void> {
  lastRecordedAudio = audio;
  moveAppState("uploading");
  queueDiagnostic({
    event: "recording-stop",
    pageInstanceId,
    mimeType: audio.mimeType,
    audioBytes: audio.blob.size,
    dataAvailable: audio.diagnostics.dataAvailable,
    stopEvent: audio.diagnostics.stopEvent,
  });
  const controller = new AbortController();
  abortController = controller;
  try {
    await streamTurn({
      audio: audio.blob,
      durationMs: audio.durationMs,
      pageInstanceId,
      csrfToken,
      context: history,
      signal: controller.signal,
      onEvent: handleSseEvent,
    });
    flushDelta();
    if (!completedReceived) throw new ApiError("The stream ended before completion.", "STREAM_INTERRUPTED");
  } catch (error) {
    flushDelta();
    if (controller.signal.aborted) {
      moveAppState("cancelled");
      statusText.textContent = "Cancelled";
      streamStatus.textContent = "Cancelled";
      if (!answerText) answerContent.textContent = "Turn cancelled.";
    } else {
      showTurnError(safeMessage(error, "The voice turn failed."), true);
    }
  } finally {
    cancelButton.classList.add("hidden");
    if (abortController === controller) abortController = null;
  }
}

function clearTurnForRecording(): void {
  flushDelta();
  lastTranscript = "";
  answerText = "";
  completedReceived = false;
  recordTimer.textContent = "00:00";
  transcription.textContent = "Waiting for speech…";
  answerContent.textContent = "Pinch again to stop recording.";
  streamStatus.textContent = "Ready";
  $("turn-meta").textContent = "MRBD browser microphone";
  retryButton.classList.add("hidden");
  renderScroll(answerScroller.jumpLatest());
}

async function retryTurn(): Promise<void> {
  retryButton.classList.add("hidden");
  if (lastTranscript) await retryModelOnly();
  else if (lastRecordedAudio) await uploadRecordedAudio(lastRecordedAudio);
  else await startBrowserRecording();
}

async function retryModelOnly(): Promise<void> {
  if (!csrfToken || !lastTranscript) return;
  moveAppState("waiting-for-model");
  const controller = new AbortController();
  abortController = controller;
  completedReceived = false;
  answerText = "";
  answerContent.textContent = "Retrying model without repeating transcription…";
  try {
    await streamTurn({
      transcript: lastTranscript,
      durationMs: 0,
      pageInstanceId,
      csrfToken,
      context: history,
      signal: controller.signal,
      onEvent: handleSseEvent,
    });
    flushDelta();
    if (!completedReceived) throw new ApiError("The stream ended before completion.", "STREAM_INTERRUPTED");
  } catch (error) {
    if (!controller.signal.aborted) showTurnError(safeMessage(error, "The model retry failed."), true);
  } finally {
    if (abortController === controller) abortController = null;
  }
}

function handleSseEvent(event: MrbdSseEvent): void {
  switch (event.event) {
    case "session":
      if (recorder.state === "stopping") moveAppState("uploading");
      if (recorder.state === "uploading") moveAppState("transcribing");
      statusText.textContent = "Audio received securely";
      break;
    case "transcription":
      lastTranscript = event.text;
      lastRecordedAudio = null;
      transcription.textContent = event.text;
      if (recorder.state === "uploading") moveAppState("transcribing");
      $("turn-meta").textContent = `Transcribed in ${formatDuration(event.durationMs)}`;
      break;
    case "model-start":
      moveAppState("waiting-for-model");
      streamStatus.textContent = "Waiting for first text";
      answerContent.textContent = "Thinking…";
      break;
    case "answer-delta":
      if (recorder.state !== "streaming") moveAppState("streaming");
      streamStatus.textContent = "Streaming";
      deltaBuffer += event.delta;
      scheduleDeltaFlush();
      break;
    case "usage":
      $("turn-meta").textContent = `${event.inputTokens + event.outputTokens} tokens · ¥${event.estimatedCostCny.toFixed(4)}`;
      break;
    case "completed": {
      flushDelta();
      completedReceived = true;
      moveAppState("completed");
      streamStatus.textContent = "Complete";
      const follow = answerScroller.contentWillUpdate();
      renderStructuredText(answerContent, answerText);
      if (lastTranscript && answerText) history = [...history, { question: lastTranscript, answer: answerText }].slice(-6);
      $("turn-meta").textContent = `${formatDuration(event.elapsedMs)} · complete`;
      renderScroll(answerScroller.contentDidUpdate(follow));
      break;
    }
    case "error":
      throw new ApiError(event.message, event.code);
  }
}

function scheduleDeltaFlush(): void {
  if (deltaTimer !== undefined) return;
  deltaTimer = window.setTimeout(flushDelta, 50);
}

function flushDelta(): void {
  if (deltaTimer !== undefined) window.clearTimeout(deltaTimer);
  deltaTimer = undefined;
  if (!deltaBuffer) return;
  const follow = answerScroller.contentWillUpdate();
  answerText += deltaBuffer;
  deltaBuffer = "";
  answerContent.textContent = answerText;
  renderScroll(answerScroller.contentDidUpdate(follow));
}

function cancelActive(reason: string): void {
  recorder.cancel(reason);
  abortController?.abort(reason);
}

function newConversation(): void {
  cancelActive("new-conversation");
  history = [];
  lastTranscript = "";
  lastRecordedAudio = null;
  answerText = "";
  transcription.textContent = "Your latest transcription appears here.";
  answerContent.textContent = "Pinch Start Recording to begin.";
  streamStatus.textContent = "Ready";
  $("turn-meta").textContent = "No turn yet";
  recorder.reset();
  updateRecordingState("idle");
  renderScroll(answerScroller.jumpLatest());
  recordButton.focus();
}

async function logoutDevice(): Promise<void> {
  try { await logout(csrfToken); }
  finally {
    session = null;
    csrfToken = "";
    newConversation();
    showScreen("pairing", "[data-digit-index='0']");
  }
}

function updateRecordingState(state: RecordingState): void {
  recordButton.dataset.state = state;
  recordLabel.textContent = STATE_LABELS[state];
  statusText.textContent = stateStatus(state);
  const busy = ["requesting-permission", "stopping", "uploading", "transcribing", "waiting-for-model", "streaming"].includes(state);
  recordButton.disabled = busy;
  if (["recording", "error", "completed", "cancelled", "idle"].includes(state)) recordButton.disabled = false;
  if (state === "recording") {
    streamStatus.textContent = "Recording";
    transcription.textContent = "Listening through the MRBD browser microphone…";
    answerContent.textContent = "Recording your question…";
    queueDiagnostic({ event: "recording-start", pageInstanceId, mimeType: recorderDiagnostics.mimeType });
    if (currentScreen === "home") requestAnimationFrame(() => recordButton.focus());
  }
  cancelButton.classList.toggle("hidden", !isTurnActive());
}

function showTurnError(message: string, retryable: boolean): void {
  moveAppState("error");
  statusText.textContent = "Error";
  streamStatus.textContent = "Failed";
  if (!answerText) answerContent.textContent = message;
  else $("turn-meta").textContent = message;
  retryButton.classList.toggle("hidden", !retryable);
  cancelButton.classList.add("hidden");
}

function renderScroll(snapshot: ScrollSnapshot): void {
  scrollProgress.textContent = `${snapshot.currentPage}/${snapshot.totalPages}`;
  newTextButton.classList.toggle("hidden", !snapshot.hasNewText);
}

async function renderDiagnostics(): Promise<void> {
  let microphonePermission = "unsupported";
  let audioInputs = "not enumerated";
  try {
    const status = await navigator.permissions?.query({ name: "microphone" as PermissionName });
    microphonePermission = status?.state ?? "unsupported";
  } catch (error) {
    microphonePermission = error instanceof Error ? error.name : "Error";
  }
  try {
    const devices = await navigator.mediaDevices?.enumerateDevices?.();
    audioInputs = String(devices?.filter((device) => device.kind === "audioinput").length ?? 0);
  } catch (error) {
    audioInputs = error instanceof Error ? error.name : "Error";
  }
  diagnosticView.textContent = [
    `pageInstanceId: ${pageInstanceId}`,
    "Capture path: pure MRBD Web App",
    `secure context: ${isSecureContext}`,
    `getUserMedia: ${typeof navigator.mediaDevices?.getUserMedia === "function" ? "present" : "missing"}`,
    `MediaRecorder: ${typeof globalThis.MediaRecorder === "function" ? "present" : "missing"}`,
    `microphone permission: ${microphonePermission}`,
    `audioinput devices: ${audioInputs}`,
    `MIME: ${recorderDiagnostics.mimeType}`,
    `dataavailable: ${recorderDiagnostics.dataAvailable}`,
    `bytes: ${recorderDiagnostics.audioBytes}`,
    `stop event: ${recorderDiagnostics.stopEvent}`,
    `error: ${recorderDiagnostics.errorName ?? "none"} · ${recorderDiagnostics.errorMessage ?? "none"}`,
    "Raw audio is never stored in browser storage",
  ].join("\n");
}

function updateNetworkState(online: boolean): void {
  queueDiagnostic({ event: "network", pageInstanceId, online });
  if (!online) statusText.textContent = "Offline";
  else if (recorder.state === "idle") statusText.textContent = "Ready";
}

function queueDiagnostic(event: DiagnosticEvent): void {
  diagnosticQueue.push(event);
  if (diagnosticQueue.length > 20) diagnosticQueue = diagnosticQueue.slice(-20);
  if (!csrfToken || diagnosticTimer !== undefined) return;
  diagnosticTimer = window.setTimeout(flushDiagnostics, 1_000);
}

function flushDiagnostics(): void {
  if (diagnosticTimer !== undefined) window.clearTimeout(diagnosticTimer);
  diagnosticTimer = undefined;
  if (!csrfToken || !diagnosticQueue.length) return;
  const batch = diagnosticQueue.splice(0, 20);
  void sendDiagnostics(csrfToken, batch).catch(() => { diagnosticQueue = [...batch, ...diagnosticQueue].slice(0, 20); });
}

function isTurnActive(): boolean {
  return ["requesting-permission", "recording", "stopping", "uploading", "transcribing", "waiting-for-model", "streaming"].includes(recorder.state);
}

function stateStatus(state: RecordingState): string {
  const values: Readonly<Record<RecordingState, string>> = {
    idle: "Ready · pinch to request microphone",
    "requesting-permission": "Requesting MRBD microphone",
    recording: "Recording · pinch to stop",
    stopping: "Finalizing browser audio",
    uploading: "Uploading securely",
    transcribing: "Alibaba ASR transcribing",
    "waiting-for-model": "Waiting for first text",
    streaming: "Answer arriving",
    completed: "Complete · ready for another turn",
    error: "Something went wrong",
    cancelled: "Cancelled",
  };
  return values[state];
}

function formatDuration(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.round(milliseconds / 1000));
  return `${String(Math.floor(totalSeconds / 60)).padStart(2, "0")}:${String(totalSeconds % 60).padStart(2, "0")}`;
}

function safeMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message || fallback : fallback;
}

function microphoneErrorMessage(error: Error): string {
  if (error.name === "NotFoundError") return "MRBD runtime exposed no audioinput device (NotFoundError).";
  if (error.name === "NotAllowedError") return "MRBD microphone permission was denied or blocked (NotAllowedError).";
  return `${error.name}: ${error.message}`;
}

void initialize();
